import React from 'react';
import { motion } from 'framer-motion';
import { FiBriefcase, FiMapPin, FiCalendar, FiArrowRight, FiArrowLeft, FiUserCheck, FiMoreVertical, FiMessageSquare } from 'react-icons/fi';
import { JobApplication, ApplicationStatus } from '../../types/application';

interface ApplicationCardProps {
  application: JobApplication;
  onCardClick: (jobId: number) => void;
  onStatusChange: (applicationId: number, newStatus: ApplicationStatus) => void;
  onMoveToInterview: (applicationId: number) => void;
}

const ApplicationCard: React.FC<ApplicationCardProps> = ({ 
  application, 
  onCardClick, 
  onStatusChange,
  onMoveToInterview 
}) => {
  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onCardClick(application.job_id);
  };

  const handleStatusChange = (e: React.MouseEvent, newStatus: ApplicationStatus) => {
    e.stopPropagation();
    onStatusChange(application.id, newStatus);
  };

  const handleMoveToInterview = (e: React.MouseEvent) => {
    e.stopPropagation();
    onMoveToInterview(application.id);
  };

  const renderActions = () => {
    switch (application.status) {
      case ApplicationStatus.TO_APPLY:
        return (
          <button
            onClick={(e) => handleStatusChange(e, ApplicationStatus.APPLIED)}
            className="flex items-center px-2 py-1 text-sm text-green-600 hover:bg-green-50 rounded-md transition-colors"
          >
            <FiArrowRight className="w-4 h-4 mr-1" />
            Mark Applied
          </button>
        );
      case ApplicationStatus.APPLIED:
        return (
          <div className="flex gap-2">
            <button
              onClick={(e) => handleStatusChange(e, ApplicationStatus.TO_APPLY)}
              className="flex items-center px-2 py-1 text-sm text-blue-600 hover:bg-blue-50 rounded-md transition-colors"
            >
              <FiArrowLeft className="w-4 h-4 mr-1" />
              Move Back
            </button>
            <button
              onClick={(e) => handleStatusChange(e, ApplicationStatus.ARCHIVED)}
              className="flex items-center px-2 py-1 text-sm text-gray-600 hover:bg-gray-50 rounded-md transition-colors"
            >
              <FiArrowRight className="w-4 h-4 mr-1" />
              Archive
            </button>
            <button
              onClick={handleMoveToInterview}
              className="flex items-center px-2 py-1 text-sm text-purple-600 hover:bg-purple-50 rounded-md"
            >
              <FiMessageSquare className="w-4 h-4 mr-1" />
              Interview
            </button>
          </div>
        );
      case ApplicationStatus.ARCHIVED:
        return (
          <button
            onClick={(e) => handleStatusChange(e, ApplicationStatus.APPLIED)}
            className="flex items-center px-2 py-1 text-sm text-blue-600 hover:bg-blue-50 rounded-md transition-colors"
          >
            <FiArrowLeft className="w-4 h-4 mr-1" />
            Unarchive
          </button>
        );
      default:
        return null;
    }
  };

  return (
    <div
      className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-all"
      onClick={handleClick}
    >
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <h3 className="font-medium text-gray-900 mb-2">{application.title}</h3>
        <div className="space-y-2">
          <div className="flex items-center text-gray-600">
            <FiBriefcase className="w-4 h-4 mr-2" />
            <span className="text-sm">{application.company}</span>
          </div>
          {application.location && (
            <div className="flex items-center text-gray-600">
              <FiMapPin className="w-4 h-4 mr-2" />
              <span className="text-sm">{application.location}</span>
            </div>
          )}
          {application.deadline && (
            <div className="flex items-center text-gray-600">
              <FiCalendar className="w-4 h-4 mr-2" />
              <span className="text-sm">Due: {application.deadline}</span>
            </div>
          )}
        </div>
        <div className="mt-4 flex justify-between items-center border-t border-gray-100 pt-3">
          <div className="flex gap-2">
            {renderActions()}
          </div>
          <motion.div
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="flex items-center text-gray-400 hover:text-gray-600 transition-colors"
          >
            <FiMoreVertical className="w-4 h-4" />
          </motion.div>
        </div>
      </motion.div>
    </div>
  );
};

export default ApplicationCard; 